import { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router';
import {
    Box, Typography, Button, Stack, Chip, CircularProgress,
    Textarea, Modal, ModalDialog, ModalClose, Alert, IconButton,
    Checkbox,
    Select,
    Option,
    Avatar,
} from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';

import ArrowBackRoundedIcon     from '@mui/icons-material/ArrowBackRounded';
import AddRoundedIcon           from '@mui/icons-material/AddRounded';
import EditRoundedIcon          from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon        from '@mui/icons-material/DeleteRounded';
import MessageRoundedIcon       from '@mui/icons-material/MessageRounded';
import ToggleOffRoundedIcon     from '@mui/icons-material/ToggleOffRounded';
import ToggleOnRoundedIcon      from '@mui/icons-material/ToggleOnRounded';
import FormatQuoteRoundedIcon   from '@mui/icons-material/FormatQuoteRounded';
import CheckRoundedIcon         from '@mui/icons-material/CheckRounded';
import SendRoundedIcon          from '@mui/icons-material/SendRounded';
import ScheduleSendRoundedIcon  from '@mui/icons-material/ScheduleSendRounded';
import PeopleRoundedIcon        from '@mui/icons-material/PeopleRounded';

import Header from '../../components/Header';
import { AppContext } from '../../context';
import {
    APIGetMotivationalMessages,
    APICreateMotivationalMessage,
    APIUpdateMotivationalMessage,
    APIDeleteMotivationalMessage,
    parseApiError,
    APICountAudience,
} from '../../api';
import MemeLoader from '../../components/MemeLoader';

// ─── Анимации ─────────────────────────────────────────────────────────────────
const pageVariants = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.38, ease: [0.22, 1, 0.36, 1] } },
    exit:    { opacity: 0, y: -12, transition: { duration: 0.22 } },
};

const stagger = {
    hidden:  {},
    visible: { transition: { staggerChildren: 0.065 } },
};

const fadeUp = {
    hidden:  { opacity: 0, y: 12 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] } },
};

const STATUS_CFG = {
    sent_instant:  { label: 'Отправлено',  color: '#22c55e', bg: 'rgba(34,197,94,0.12)', isHistory: true  },
    sent_once:     { label: 'Отправлено',  color: '#22c55e', bg: 'rgba(34,197,94,0.12)', isHistory: true  },
    pending:       { label: 'Запланировано',color:'#f59e0b', bg: 'rgba(245,158,11,0.12)', isHistory: false },
    recurring:     { label: 'Активно',     color: '#3b82f6', bg: 'rgba(59,130,246,0.12)', isHistory: false },
    inactive:      { label: 'Неактивно',   color: '#5a5a7a', bg: 'rgba(90,90,122,0.12)',  isHistory: false },
};

function fmtDateTime(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mn = String(d.getMinutes()).padStart(2, '0');
    return `${dd}.${mm} ${hh}:${mn}`;
}

function MessageCard({ message, onEdit, onDelete, onToggle, canUpdate, canDelete }) {
    // Используем серверный status; на всякий случай — fallback для старых записей
    const status =
        message.status
        || (message.send_type === 'instant' ? 'sent_instant'
            : message.repeat_mode === 'once' && message.is_sent ? 'sent_once'
            : !message.is_active ? 'inactive'
            : message.repeat_mode !== 'once' ? 'recurring'
            : 'pending');

    const cfg = STATUS_CFG[status] || STATUS_CFG.inactive;
    const isHistory = cfg.isHistory;
    // edit/delete/toggle разрешены только если это НЕ история
    const showActions = !isHistory;

    return (
        <motion.div variants={fadeUp}>
            <Box sx={{
                position:     'relative',
                px:           2.5,
                py:           2,
                borderRadius: 'var(--joy-radius-xl)',
                border:       '1px solid',
                borderColor:  isHistory ? 'rgba(90,90,122,0.25)' : 'var(--joy-palette-divider)',
                bgcolor:      isHistory ? 'rgba(30,30,53,0.5)'    : 'var(--joy-palette-background-level1)',
                transition:   'all .18s',
                opacity:      isHistory ? 0.75 : 1,
                '&:hover':    { borderColor: 'rgba(162,38,226,0.3)', opacity: 1 },
            }}>
                <Box sx={{ position: 'absolute', top: 12, left: 14, color: 'rgba(162,38,226,0.2)', lineHeight: 1 }}>
                    <FormatQuoteRoundedIcon sx={{ fontSize: 28 }} />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Box sx={{ flex: 1, minWidth: 0, pl: 2.5 }}>
                        <Typography sx={{
                            fontSize: 13.5, lineHeight: 1.65,
                            color: 'var(--joy-palette-text-primary)',
                            fontStyle: 'italic',
                        }}>
                            {message.text}
                        </Typography>

                        {/* Бейджи */}
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.8, flexWrap: 'wrap' }}>
                            {/* Тип */}
                            <Chip size="sm" sx={{
                                fontSize: 9, fontWeight: 700, border: 'none',
                                bgcolor: message.send_type === 'instant' ? 'rgba(245,158,11,0.12)' : 'rgba(162,38,226,0.12)',
                                color:   message.send_type === 'instant' ? '#f59e0b' : 'var(--joy-palette-primary-300)',
                            }}>
                                {message.send_type === 'instant' ? 'Мгновенное' : 'Отложенное'}
                            </Chip>

                            {/* Статус */}
                            <Chip size="sm" sx={{
                                fontSize: 9, fontWeight: 700, border: 'none',
                                bgcolor: cfg.bg, color: cfg.color,
                            }}>
                                {cfg.label}
                                {isHistory && message.sent_at && ` · ${fmtDateTime(message.sent_at)}`}
                                {isHistory && typeof message.sent_count === 'number' && ` · ${message.sent_count} получ.`}
                                {status === 'pending' && message.scheduled_at && ` · ${fmtDateTime(message.scheduled_at)}`}
                            </Chip>

                            {/* Повторяемость */}
                            {message.repeat_mode === 'daily' && (
                                <Chip size="sm" sx={{ fontSize: 9, fontWeight: 600, border: 'none', bgcolor: 'rgba(34,197,94,0.12)', color: '#22c55e' }}>
                                    Ежедневно
                                </Chip>
                            )}
                            {message.repeat_mode === 'weekly' && (
                                <Chip size="sm" sx={{ fontSize: 9, fontWeight: 600, border: 'none', bgcolor: 'rgba(59,130,246,0.12)', color: '#3b82f6' }}>
                                    Еженедельно
                                </Chip>
                            )}
                        </Box>
                    </Box>

                    {/* Кнопки — только для НЕ-истории */}
                    {showActions && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flexShrink: 0 }}>
                            {canUpdate && (
                                <IconButton
                                    size="sm" variant="soft"
                                    title={message.is_active ? 'Деактивировать' : 'Активировать'}
                                    onClick={() => onToggle(message)}
                                    sx={{
                                        bgcolor: message.is_active ? 'rgba(9,192,15,0.5)' : 'rgba(90,90,122,0.5)',
                                        borderRadius: 'var(--joy-radius-md)',
                                    }}
                                >
                                    {message.is_active
                                        ? <ToggleOnRoundedIcon  sx={{ fontSize: 20, color: '#22c55e' }} />
                                        : <ToggleOffRoundedIcon sx={{ fontSize: 20, color: 'var(--joy-palette-text-tertiary)' }} />}
                                </IconButton>
                            )}
                            {canUpdate && (
                                <IconButton
                                    size="sm" variant="soft" onClick={() => onEdit(message)}
                                    sx={{ bgcolor: 'rgba(162,38,226,0.5)', borderRadius: 'var(--joy-radius-md)' }}
                                >
                                    <EditRoundedIcon sx={{ fontSize: 20, color: 'var(--joy-palette-primary-400)' }} />
                                </IconButton>
                            )}
                            {canDelete && (
                                <IconButton
                                    size="sm" variant="soft" onClick={() => onDelete(message)}
                                    sx={{ bgcolor: 'rgba(248,113,113,0.5)', borderRadius: 'var(--joy-radius-md)' }}
                                >
                                    <DeleteRoundedIcon sx={{ fontSize: 20, color: '#f87171' }} />
                                </IconButton>
                            )}
                        </Box>
                    )}
                </Box>
            </Box>
        </motion.div>
    );
}


// ─── DAYS_RU для выбора дня недели ──────────────────────────────────────────
const WEEKDAYS_RU = ['Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье'];

// ─── Модалка: создать / редактировать ────────────────────────────────────────
function MessageModal({ open, message, onClose, onSave, loading, error, token, canCreate, canUpdate }) {
    const isEdit = !!message;

    const [text, setText]                     = useState('');
    const [sendType, setSendType]             = useState('instant');    // instant | scheduled
    const [audience, setAudience]             = useState('photographers');
    const [audienceFilters, setAudienceFilters] = useState({});
    const [repeatMode, setRepeatMode]         = useState('once');
    const [repeatWeekday, setRepeatWeekday]   = useState(5);           // суббота по умолчанию
    const [repeatTime, setRepeatTime]         = useState('10:00');
    const [scheduledDate, setScheduledDate]   = useState('');
    const [scheduledTime, setScheduledTime]   = useState('10:00');
    const [recipientCount, setRecipientCount] = useState(null);
    const [recipientList, setRecipientList]   = useState([]);      // полный список получателей
    const [selectedIds, setSelectedIds]       = useState(new Set()); // отмеченные галочками
    const [showRecipients, setShowRecipients] = useState(false);    // развёрнут ли список
    const [countLoading, setCountLoading]     = useState(false);

    // Инициализация при открытии
    useEffect(() => {
        if (!open) return;
        if (message) {
            setText(message.text || '');
            setSendType(message.send_type || 'scheduled');
            setAudience(message.audience || 'photographers');
            setAudienceFilters(message.audience_filters || {});
            setRepeatMode(message.repeat_mode || 'once');
            setRepeatWeekday(message.repeat_weekday ?? 5);
            setRepeatTime(message.repeat_time?.slice(0,5) || '10:00');
            if (message.scheduled_at) {
                const dt = new Date(message.scheduled_at);
                setScheduledDate(dt.toISOString().slice(0, 10));
                setScheduledTime(dt.toTimeString().slice(0, 5));
            }
        } else {
            setText('');
            setSendType('instant');
            setAudience('photographers');
            setAudienceFilters({});
            setRepeatMode('once');
            setRepeatWeekday(5);
            setRepeatTime('10:00');
            setScheduledDate('');
            setScheduledTime('10:00');
        }
        setRecipientCount(null);
        setRecipientList([]);
        setSelectedIds(new Set());
        setShowRecipients(false);
    }, [open, message]);

    // Подсчёт аудитории при смене фильтров
    useEffect(() => {
        if (!open || !token) return;
        const timer = setTimeout(async () => {
            setCountLoading(true);
            const res = await APICountAudience(token, {
                audience,
                audience_filters: audienceFilters,
                include_list: true,
            });
            const count = res?.count ?? 0;
            const list  = res?.recipients ?? [];
            setRecipientCount(count);
            setRecipientList(list);
            // По умолчанию все выбраны
            setSelectedIds(new Set(list.map(r => r.id)));
            setShowRecipients(false);
            setCountLoading(false);
        }, 400);
        return () => clearTimeout(timer);
    }, [audience, audienceFilters, open, token]);

    const charCount = text.length;
    const isOk = charCount >= 5 && selectedIds.size > 0;

    const handleFilterChange = (key, value) => {
        setAudienceFilters(prev => {
            const next = { ...prev };
            if (value === '' || value === null || value === undefined || value === false) {
                delete next[key];
            } else {
                next[key] = value;
            }
            return next;
        });
    };

    const handleToggleRecipient = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleSelectAll = () => {
        setSelectedIds(new Set(recipientList.map(r => r.id)));
    };

    const handleDeselectAll = () => {
        setSelectedIds(new Set());
    };

    const handleSubmit = () => {
        if (selectedIds.size === 0) return;

        const payload = {
            text: text.trim(),
            send_type: sendType,
            audience,
            audience_filters: audienceFilters,
            repeat_mode: repeatMode,
        };

        // Если убрали кого-то из списка — передаём конкретные ID
        if (recipientList.length > 0 && selectedIds.size < recipientList.length) {
            payload.recipient_ids = [...selectedIds];
        }

        if (sendType === 'scheduled') {
            if (repeatMode === 'once' && scheduledDate) {
                payload.scheduled_at = `${scheduledDate}T${scheduledTime || '10:00'}:00`;
            }
            if (repeatMode === 'daily') {
                payload.repeat_time = repeatTime || '10:00';
            }
            if (repeatMode === 'weekly') {
                payload.repeat_weekday = repeatWeekday;
                payload.repeat_time = repeatTime || '10:00';
            }
        }

        onSave(payload);
    };

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog sx={{
                bgcolor: 'var(--joy-palette-background-level1)',
                border: '1px solid var(--joy-palette-divider)',
                borderRadius: 'var(--joy-radius-xl)',
                boxShadow: '0 24px 64px rgba(0,0,0,0.7)',
                maxWidth: '95vw', width: 520, p: 3,
                maxHeight: '90vh', overflow: 'auto',
            }}>
                <ModalClose sx={{ color: 'var(--joy-palette-text-tertiary)' }} />

                {((!isEdit && canCreate) || (isEdit && canUpdate)) && (
                    <Box sx={{ mb: 2 }}>
                        <Typography sx={{ fontSize: 18, fontWeight: 800, color: 'var(--joy-palette-text-primary)' }}>
                            {isEdit ? 'Редактировать' : 'Новое сообщение'}
                        </Typography>
                    </Box>
                )}

                <Stack spacing={2}>

                    {/* ── Тип: Сейчас / Отложенное ──────────────────────── */}
                    {!isEdit && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Box
                                onClick={() => setSendType('instant')}
                                sx={{
                                    flex: 1, py: 1.2, textAlign: 'center',
                                    borderRadius: 'var(--joy-radius-lg)',
                                    border: '2px solid',
                                    borderColor: sendType === 'instant' ? '#f59e0b' : 'var(--joy-palette-divider)',
                                    bgcolor: sendType === 'instant' ? 'rgba(245,158,11,0.1)' : 'transparent',
                                    cursor: 'pointer', transition: 'all .15s',
                                }}
                            >
                                <SendRoundedIcon sx={{ fontSize: 18, color: sendType === 'instant' ? '#f59e0b' : 'var(--joy-palette-text-tertiary)', mb: 0.3 }} />
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: sendType === 'instant' ? '#f59e0b' : 'var(--joy-palette-text-secondary)' }}>
                                    Отправить сейчас
                                </Typography>
                            </Box>
                            <Box
                                onClick={() => setSendType('scheduled')}
                                sx={{
                                    flex: 1, py: 1.2, textAlign: 'center',
                                    borderRadius: 'var(--joy-radius-lg)',
                                    border: '2px solid',
                                    borderColor: sendType === 'scheduled' ? 'var(--joy-palette-primary-500)' : 'var(--joy-palette-divider)',
                                    bgcolor: sendType === 'scheduled' ? 'rgba(162,38,226,0.1)' : 'transparent',
                                    cursor: 'pointer', transition: 'all .15s',
                                }}
                            >
                                <ScheduleSendRoundedIcon sx={{ fontSize: 18, color: sendType === 'scheduled' ? 'var(--joy-palette-primary-400)' : 'var(--joy-palette-text-tertiary)', mb: 0.3 }} />
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: sendType === 'scheduled' ? 'var(--joy-palette-primary-300)' : 'var(--joy-palette-text-secondary)' }}>
                                    Отложенное
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    {/* ── Текст сообщения ───────────────────────────────── */}
                    <Box>
                        <Textarea
                            value={text}
                            onChange={e => setText(e.target.value)}
                            placeholder="Текст сообщения…"
                            minRows={3} maxRows={6}
                            sx={{
                                bgcolor: 'var(--joy-palette-background-level2)',
                                border: '1px solid var(--joy-palette-divider)',
                                fontSize: 13, color: 'white',
                                '&:focus-within': { borderColor: 'var(--joy-palette-primary-500)' },
                            }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.3 }}>
                            <Typography sx={{ fontSize: 10, color: charCount < 5 ? '#f87171' : 'var(--joy-palette-text-tertiary)' }}>
                                {charCount} символов
                            </Typography>
                        </Box>
                    </Box>

                    {/* ── Аудитория ─────────────────────────────────────── */}
                    <Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--joy-palette-text-secondary)', mb: 0.5 }}>
                            Кому
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.8 }}>
                            {[
                                { val: 'all', label: 'Все' },
                                { val: 'photographers', label: 'Фотографы' },
                                { val: 'managers', label: 'Руководители' },
                            ].map(opt => (
                                <Box
                                    key={opt.val}
                                    onClick={() => { setAudience(opt.val); setAudienceFilters({}); }}
                                    sx={{
                                        px: 1.5, py: 0.6,
                                        borderRadius: 'var(--joy-radius-md)',
                                        border: '1px solid',
                                        borderColor: audience === opt.val ? 'var(--joy-palette-primary-500)' : 'var(--joy-palette-divider)',
                                        bgcolor: audience === opt.val ? 'rgba(162,38,226,0.1)' : 'transparent',
                                        cursor: 'pointer', transition: 'all .15s',
                                    }}
                                >
                                    <Typography sx={{
                                        fontSize: 11, fontWeight: audience === opt.val ? 700 : 500,
                                        color: audience === opt.val ? 'var(--joy-palette-primary-300)' : 'var(--joy-palette-text-secondary)',
                                    }}>
                                        {opt.label}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    {/* ── Доп. фильтры для фотографов ──────────────────── */}
                    {audience === 'photographers' && (
                        <Box sx={{
                            p: 1.5, borderRadius: 'var(--joy-radius-md)',
                            bgcolor: 'var(--joy-palette-background-level2)',
                            border: '1px solid var(--joy-palette-divider)',
                        }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', mb: 1 }}>
                                Фильтры (необязательно)
                            </Typography>
                            <Stack spacing={1}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Checkbox size="sm"
                                        checked={!!audienceFilters.assigned_on_date}
                                        onChange={(e) => handleFilterChange('assigned_on_date', e.target.checked ? new Date().toISOString().slice(0,10) : null)}
                                    />
                                    <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-primary)', flex: 1 }}>
                                        У кого назначено на дату
                                    </Typography>
                                    {audienceFilters.assigned_on_date && (
                                        <Box component="input" type="date"
                                            value={audienceFilters.assigned_on_date}
                                            onChange={(e) => handleFilterChange('assigned_on_date', e.target.value)}
                                            sx={{ fontSize: 11, bgcolor: 'var(--joy-palette-background-level1)', color: 'white', border: '1px solid var(--joy-palette-divider)', borderRadius: 4, px: 0.5 }}
                                        />
                                    )}
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Checkbox size="sm"
                                        checked={!!audienceFilters.not_assigned_on_date}
                                        onChange={(e) => handleFilterChange('not_assigned_on_date', e.target.checked ? new Date().toISOString().slice(0,10) : null)}
                                    />
                                    <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-primary)', flex: 1 }}>
                                        У кого нет назначения на дату
                                    </Typography>
                                    {audienceFilters.not_assigned_on_date && (
                                        <Box component="input" type="date"
                                            value={audienceFilters.not_assigned_on_date}
                                            onChange={(e) => handleFilterChange('not_assigned_on_date', e.target.value)}
                                            sx={{ fontSize: 11, bgcolor: 'var(--joy-palette-background-level1)', color: 'white', border: '1px solid var(--joy-palette-divider)', borderRadius: 4, px: 0.5 }}
                                        />
                                    )}
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Checkbox size="sm"
                                        checked={!!audienceFilters.no_assignment_days}
                                        onChange={(e) => handleFilterChange('no_assignment_days', e.target.checked ? 7 : null)}
                                    />
                                    <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-primary)', flex: 1 }}>
                                        Давно не назначались (дней)
                                    </Typography>
                                    {audienceFilters.no_assignment_days && (
                                        <Box component="input" type="number" inputMode="numeric" min="1" max="90"
                                            value={audienceFilters.no_assignment_days}
                                            onChange={(e) => handleFilterChange('no_assignment_days', parseInt(e.target.value) || 7)}
                                            sx={{ width: 50, fontSize: 11, bgcolor: 'var(--joy-palette-background-level1)', color: 'white', border: '1px solid var(--joy-palette-divider)', borderRadius: 4, px: 0.5, textAlign: 'center' }}
                                        />
                                    )}
                                </Box>
                            </Stack>
                        </Box>
                    )}

                    {/* ── Получатели с возможностью выбора ────────────── */}
                    <Box sx={{
                        borderRadius: 'var(--joy-radius-md)',
                        bgcolor: 'rgba(162,38,226,0.06)',
                        border: '1px solid rgba(162,38,226,0.15)',
                        overflow: 'hidden',
                    }}>
                        <Box
                            onClick={() => !countLoading && recipientList.length > 0 && setShowRecipients(prev => !prev)}
                            sx={{
                                display: 'flex', alignItems: 'center', gap: 1,
                                px: 1.5, py: 1,
                                cursor: recipientList.length > 0 ? 'pointer' : 'default',
                                transition: 'background .15s',
                                '&:hover': recipientList.length > 0 ? { bgcolor: 'rgba(162,38,226,0.1)' } : {},
                            }}
                        >
                            <PeopleRoundedIcon sx={{ fontSize: 16, color: 'var(--joy-palette-primary-400)' }} />
                            <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)', flex: 1 }}>
                                Получателей:
                            </Typography>
                            {countLoading
                                ? <MemeLoader />
                                : <>
                                    <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'var(--joy-palette-primary-300)' }}>
                                        {selectedIds.size}
                                    </Typography>
                                    {recipientList.length > 0 && selectedIds.size !== recipientList.length && (
                                        <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                                            из {recipientList.length}
                                        </Typography>
                                    )}
                                    {recipientList.length > 0 && (
                                        <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)', ml: 0.5 }}>
                                            {showRecipients ? '▲' : '▼'}
                                        </Typography>
                                    )}
                                </>
                            }
                        </Box>

                        {showRecipients && recipientList.length > 0 && (
                            <Box sx={{
                                px: 1.5, pb: 1.5, pt: 0.5,
                                borderTop: '1px solid rgba(162,38,226,0.1)',
                                maxHeight: 180, overflowY: 'auto',
                            }}>
                                {/* Кнопки Все / Никого */}
                                <Box sx={{ display: 'flex', gap: 1, mb: 0.8 }}>
                                    <Typography
                                        onClick={handleSelectAll}
                                        sx={{ fontSize: 10, fontWeight: 700, color: 'var(--joy-palette-primary-300)', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                    >
                                        Выбрать всех
                                    </Typography>
                                    <Typography
                                        onClick={handleDeselectAll}
                                        sx={{ fontSize: 10, fontWeight: 700, color: 'var(--joy-palette-text-tertiary)', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                    >
                                        Снять все
                                    </Typography>
                                </Box>

                                <Stack spacing={0.3}>
                                    {recipientList.map(r => (
                                        <Box
                                            key={r.id}
                                            onClick={() => handleToggleRecipient(r.id)}
                                            sx={{
                                                display: 'flex', alignItems: 'center', gap: 1,
                                                px: 1, py: 0.5,
                                                borderRadius: 'var(--joy-radius-sm)',
                                                cursor: 'pointer',
                                                transition: 'background .12s',
                                                bgcolor: selectedIds.has(r.id) ? 'rgba(162,38,226,0.08)' : 'transparent',
                                                '&:hover': { bgcolor: 'rgba(162,38,226,0.12)' },
                                            }}
                                        >
                                            <Checkbox
                                                size="sm"
                                                checked={selectedIds.has(r.id)}
                                                onChange={() => handleToggleRecipient(r.id)}
                                                sx={{ pointerEvents: 'none' }}
                                            />
                                            <Avatar src={r.avatar_url} size="sm" sx={{
                                                width: 24, height: 24, fontSize: 10, fontWeight: 700,
                                                bgcolor: 'var(--joy-palette-background-level3)', color: 'var(--joy-palette-text-tertiary)',
                                                border: '1px solid var(--joy-palette-divider)', flexShrink: 0,
                                            }}>
                                                {(r.full_name || '').charAt(0).toUpperCase()}
                                            </Avatar>
                                            <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-primary)', flex: 1 }}>
                                                {r.full_name}
                                            </Typography>
                                            <Typography sx={{ fontSize: 9, color: 'var(--joy-palette-text-tertiary)' }}>
                                                {r.role === 'manager' ? 'Рук.' : 'Фото'}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>
                        )}
                    </Box>

                    {/* ── Повторяемость ─────────────────────────────────── */}
                    {sendType === 'scheduled' && (
                        <Box>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--joy-palette-text-secondary)', mb: 0.5 }}>
                                Повторяемость
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.8, mb: 1 }}>
                                {[
                                    { val: 'once', label: 'Один раз' },
                                    { val: 'daily', label: 'Ежедневно' },
                                    { val: 'weekly', label: 'Еженедельно' },
                                ].map(opt => (
                                    <Box
                                        key={opt.val}
                                        onClick={() => setRepeatMode(opt.val)}
                                        sx={{
                                            px: 1.5, py: 0.6,
                                            borderRadius: 'var(--joy-radius-md)',
                                            border: '1px solid',
                                            borderColor: repeatMode === opt.val ? 'var(--joy-palette-primary-500)' : 'var(--joy-palette-divider)',
                                            bgcolor: repeatMode === opt.val ? 'rgba(162,38,226,0.1)' : 'transparent',
                                            cursor: 'pointer', transition: 'all .15s',
                                        }}
                                    >
                                        <Typography sx={{
                                            fontSize: 11, fontWeight: repeatMode === opt.val ? 700 : 500,
                                            color: repeatMode === opt.val ? 'var(--joy-palette-primary-300)' : 'var(--joy-palette-text-secondary)',
                                        }}>
                                            {opt.label}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>

                            {repeatMode === 'once' && (
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', mb: 0.3 }}>Дата</Typography>
                                        <Box component="input" type="date" value={scheduledDate}
                                            onChange={(e) => setScheduledDate(e.target.value)}
                                            sx={{ width: '100%', px: 1, py: 0.7, bgcolor: 'var(--joy-palette-background-level2)', color: 'white', border: '1px solid var(--joy-palette-divider)', borderRadius: 'var(--joy-radius-md)', fontSize: 12, fontFamily: 'inherit' }}
                                        />
                                    </Box>
                                    <Box sx={{ width: 100 }}>
                                        <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', mb: 0.3 }}>Время</Typography>
                                        <Box component="input" type="time" value={scheduledTime}
                                            onChange={(e) => setScheduledTime(e.target.value)}
                                            sx={{ width: '100%', px: 1, py: 0.7, bgcolor: 'var(--joy-palette-background-level2)', color: 'white', border: '1px solid var(--joy-palette-divider)', borderRadius: 'var(--joy-radius-md)', fontSize: 12, fontFamily: 'inherit' }}
                                        />
                                    </Box>
                                </Box>
                            )}

                            {repeatMode === 'daily' && (
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', mb: 0.3 }}>Время отправки</Typography>
                                        <Box component="input" type="time" value={repeatTime}
                                            onChange={(e) => setRepeatTime(e.target.value)}
                                            sx={{ width: '100%', px: 1, py: 0.7, bgcolor: 'var(--joy-palette-background-level2)', color: 'white', border: '1px solid var(--joy-palette-divider)', borderRadius: 'var(--joy-radius-md)', fontSize: 12, fontFamily: 'inherit' }}
                                        />
                                    </Box>
                                </Box>
                            )}

                            {repeatMode === 'weekly' && (
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', mb: 0.3 }}>День недели</Typography>
                                        <Select
                                            size='sm'
                                            value={repeatWeekday}
                                            onChange={(_, v) => setRepeatWeekday(v)}
                                            sx={{
                                                color: 'var(--joy-palette-text-primary)',
                                                bgcolor: 'var(--joy-palette-background-level2)',
                                                border: '1px solid var(--joy-palette-divider)',
                                                fontSize: 13,
                                                '&:hover': { borderColor: 'rgba(162,38,226,0.4)', bgcolor: 'var(--joy-palette-background-level2)' },
                                            }}
                                            slotProps={{
                                                listbox: {
                                                    sx: {
                                                        bgcolor: 'var(--joy-palette-background-level2)',
                                                        border: '1px solid var(--joy-palette-divider)',
                                                        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                                                        '& .MuiOption-root': {
                                                            color: 'var(--joy-palette-text-primary)',
                                                            fontSize: 13,
                                                            '&:hover': {
                                                                bgcolor: 'rgba(162,38,226,0.12)',
                                                                color: 'var(--joy-palette-text-primary)',
                                                            },
                                                            '&[aria-selected="true"]': {
                                                                bgcolor: 'rgba(162,38,226,0.2)',
                                                                color: 'var(--joy-palette-primary-300)',
                                                                fontWeight: 600,
                                                            },
                                                        },
                                                    },
                                                },
                                            }}
                                        >
                                            {WEEKDAYS_RU.map((d, i) => (
                                                <Option key={i} value={i}>{d}</Option>
                                            ))}
                                        </Select>
                                    </Box>
                                    <Box sx={{ width: 100 }}>
                                        <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', mb: 0.3 }}>Время</Typography>
                                        <Box component="input" type="time" value={repeatTime}
                                            onChange={(e) => setRepeatTime(e.target.value)}
                                            sx={{ width: '100%', px: 1, py: 0.7, bgcolor: 'var(--joy-palette-background-level2)', color: 'white', border: '1px solid var(--joy-palette-divider)', borderRadius: 'var(--joy-radius-md)', fontSize: 12, fontFamily: 'inherit' }}
                                        />
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}

                    {error && (
                        <Alert color="danger" variant="soft" size="sm" sx={{ borderRadius: 'var(--joy-radius-md)', fontSize: 12 }}>
                            {error}
                        </Alert>
                    )}

                    {/* ── Кнопки ────────────────────────────────────────── */}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            onClick={handleSubmit}
                            loading={loading}
                            disabled={!isOk}
                            startDecorator={sendType === 'instant' && !isEdit ? <SendRoundedIcon sx={{ fontSize: 15 }} /> : <CheckRoundedIcon sx={{ fontSize: 15 }} />}
                            sx={{
                                flex: 1,
                                background: sendType === 'instant' && !isEdit
                                    ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                                    : 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                                color: '#fff', fontWeight: 700, border: 'none',
                                '&:disabled': { opacity: 0.45 },
                            }}
                        >
                            {isEdit ? 'Сохранить' : sendType === 'instant' ? 'Отправить сейчас' : 'Запланировать'}
                        </Button>
                        <Button variant="outlined" onClick={onClose}
                            sx={{ borderColor: 'var(--joy-palette-divider)', color: 'var(--joy-palette-text-secondary)', fontWeight: 600 }}>
                            Отмена
                        </Button>
                    </Box>
                </Stack>
            </ModalDialog>
        </Modal>
    );
}


// ─── Модалка: удаление ────────────────────────────────────────────────────────
function DeleteModal({ open, onClose, onConfirm, loading }) {
    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog sx={{
                bgcolor: 'var(--joy-palette-background-level1)',
                border: '1px solid rgba(248,113,113,0.3)',
                borderRadius: 'var(--joy-radius-xl)',
                boxShadow: '0 24px 64px rgba(0,0,0,0.7)',
                maxWidth: 340, width: '100%', p: 3,
                maxHeight: 'calc(100dvh - 32px)', overflowY: 'auto',
            }}>
                <ModalClose sx={{ color: 'var(--joy-palette-text-tertiary)' }} />

                <Box sx={{ textAlign: 'center', mb: 2.5 }}>
                    <Box sx={{
                        width: 52, height: 52, borderRadius: '50%',
                        bgcolor: 'rgba(248,113,113,0.12)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        mx: 'auto', mb: 1.5,
                    }}>
                        <DeleteRoundedIcon sx={{ fontSize: 24, color: '#f87171' }} />
                    </Box>
                    <Typography sx={{ fontSize: 15, fontWeight: 800, color: 'var(--joy-palette-text-primary)' }}>
                        Удалить сообщение?
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', mt: 0.5 }}>
                        Это действие нельзя отменить
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        onClick={onConfirm}
                        loading={loading}
                        sx={{
                            flex: 1,
                            background: 'linear-gradient(135deg, #dc2626 0%, #f87171 100%)',
                            color: '#fff', fontWeight: 700, border: 'none',
                            boxShadow: '0 4px 16px rgba(248,113,113,0.35)',
                        }}
                    >
                        Удалить
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={onClose}
                        sx={{ borderColor: 'var(--joy-palette-divider)', color: 'var(--joy-palette-text-secondary)', fontWeight: 600 }}
                    >
                        Отмена
                    </Button>
                </Box>
            </ModalDialog>
        </Modal>
    );
}

// ════════════════════════════════════════════════════════════════════
//  MessagesPage
// ════════════════════════════════════════════════════════════════════
export default function MessagesPage() {
    const { token, showToast, hasPerm } = useContext(AppContext);
    const navigate = useNavigate();

    const canCreate = hasPerm('system.messages.create');
    const canUpdate = hasPerm('system.messages.update');
    const canDelete = hasPerm('system.messages.delete');

    const [messages,  setMessages]  = useState([]);
    const [loading,   setLoading]   = useState(true);
    const [filter,    setFilter]    = useState('pending'); // all | active | inactive

    const [editModal,   setEditModal]   = useState({ open: false, message: null, loading: false, error: '' });
    const [deleteModal, setDeleteModal] = useState({ open: false, message: null, loading: false });

    const loadMessages = useCallback(async () => {
        setLoading(true);
        const data = await APIGetMotivationalMessages(token);
        if (Array.isArray(data)) setMessages(data);
        setLoading(false);
    }, [token]);

    useEffect(() => { loadMessages(); }, [loadMessages]);

    const filtered = messages.filter(m => {
        const s = m.status || (
            m.send_type === 'instant' ? 'sent_instant'
            : m.repeat_mode === 'once' && m.is_sent ? 'sent_once'
            : !m.is_active ? 'inactive'
            : m.repeat_mode !== 'once' ? 'recurring'
            : 'pending'
        );
        if (filter === 'all')       return true;
        if (filter === 'pending')   return s === 'pending';
        if (filter === 'recurring') return s === 'recurring';
        if (filter === 'history')   return s === 'sent_instant' || s === 'sent_once';
        if (filter === 'inactive')  return s === 'inactive';
        return true;
    });

    const pendingCount   = messages.filter(m => (m.status || '') === 'pending').length;
    const recurringCount = messages.filter(m => (m.status || '') === 'recurring').length;
    const historyCount   = messages.filter(m => ['sent_instant','sent_once'].includes(m.status || '')).length;
    const inactiveCount  = messages.filter(m => (m.status || '') === 'inactive').length;
    const activeCount   = messages.filter(m => m.is_active).length;

    // ── Сохранить ─────────────────────────────────────────────────
    const handleSave = async (payload) => {
        const isEdit = !!editModal.message;
        // payload может быть строкой (обратная совместимость) или объектом
        const text = typeof payload === 'string' ? payload : payload?.text;
        if (!text?.trim()) {
            setEditModal(prev => ({ ...prev, error: 'Текст не может быть пустым' }));
            return;
        }
        setEditModal(prev => ({ ...prev, loading: true, error: '' }));

        const res = isEdit
            ? await APIUpdateMotivationalMessage(token, editModal.message.id, typeof payload === 'object' ? payload : { text: text.trim() })
            : await APICreateMotivationalMessage(token, typeof payload === 'object' ? payload : text.trim());

        if (res?.error) {
            const msg = parseApiError(res.error, 'Не удалось сохранить сообщение');
            setEditModal(prev => ({ ...prev, loading: false, error: msg }));
            return;
        }

        showToast(isEdit ? 'Сообщение обновлено ✓' : 'Сообщение добавлено ✓');
        setEditModal({ open: false, message: null, loading: false, error: '' });
        loadMessages();
    };

    // ── Переключить активность ────────────────────────────────────
    const handleToggle = async (msg) => {
        const res = await APIUpdateMotivationalMessage(token, msg.id, { is_active: !msg.is_active });
        if (!res?.error) {
            showToast(msg.is_active ? 'Деактивировано' : 'Активировано ✓', msg.is_active ? 'warning' : 'success');
            loadMessages();
        } else {
            showToast('Ошибка при обновлении', 'danger');
        }
    };

    // ── Удалить ───────────────────────────────────────────────────
    const handleDelete = async () => {
        setDeleteModal(prev => ({ ...prev, loading: true }));
        const res = await APIDeleteMotivationalMessage(token, deleteModal.message.id);
        setDeleteModal({ open: false, message: null, loading: false });
        if (res?.error) {
            showToast(parseApiError(res.error, 'Ошибка при удалении'), 'danger');
            return;
        }
        showToast('Сообщение удалено', 'warning');
        loadMessages();
    };

    return (
        <motion.div
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}
        >
            <Header />

            <Box sx={{ maxWidth: 780, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>

                {/* Заголовок */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.04 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box
                                onClick={() => navigate('/manager')}
                                sx={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    width: 36, height: 36,
                                    borderRadius: 'var(--joy-radius-md)',
                                    border: '1px solid var(--joy-palette-divider)',
                                    bgcolor: 'var(--joy-palette-background-level1)',
                                    cursor: 'pointer', transition: 'all .15s',
                                    '&:hover': { borderColor: 'var(--joy-palette-primary-500)', bgcolor: 'rgba(162,38,226,0.08)' },
                                }}
                            >
                                <ArrowBackRoundedIcon sx={{ fontSize: 18, color: 'var(--joy-palette-text-secondary)' }} />
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--joy-palette-text-primary)' }}>
                                    Сообщения
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1.5, mt: 0.3, alignItems: 'center', flexWrap: 'wrap' }}>
                                    {pendingCount > 0 && (
                                        <Typography sx={{ fontSize: 11, color: '#f59e0b' }}>{pendingCount} запланировано</Typography>
                                    )}
                                    {recurringCount > 0 && (
                                        <Typography sx={{ fontSize: 11, color: '#3b82f6' }}>{recurringCount} активных</Typography>
                                    )}
                                    {historyCount > 0 && (
                                        <Typography sx={{ fontSize: 11, color: '#22c55e' }}>{historyCount} в истории</Typography>
                                    )}
                                    {inactiveCount > 0 && (
                                        <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>{inactiveCount} неактивных</Typography>
                                    )}
                                </Box>
                            </Box>
                        </Box>

                        {canCreate && (
                            <Button
                                startDecorator={<AddRoundedIcon sx={{ fontSize: 16 }} />}
                                onClick={() => setEditModal({ open: true, message: null, loading: false, error: '' })}
                                sx={{
                                    background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                                    color: '#fff', fontWeight: 700, fontSize: 13,
                                    border: 'none',
                                    boxShadow: '0 4px 16px rgba(162,38,226,0.35)',
                                    '&:hover': { boxShadow: '0 6px 24px rgba(162,38,226,0.5)' },
                                }}
                            >
                                Добавить
                            </Button>
                        )}
                    </Box>
                </motion.div>

                {/* Инфо-баннер */}
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.06 }}>
                    <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        px: 2, py: 1.4, mb: 2.5,
                        borderRadius: 'var(--joy-radius-lg)',
                        bgcolor: 'rgba(162,38,226,0.06)',
                        border: '1px solid rgba(162,38,226,0.15)',
                    }}>
                        <MessageRoundedIcon sx={{ fontSize: 16, color: 'var(--joy-palette-primary-400)', flexShrink: 0 }} />
                        <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)' }}>
                            Мгновенные сообщения отправляются сразу. Отложенные — в указанное время воркером.
                        </Typography>
                    </Box>
                </motion.div>

                {/* Фильтр */}
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.28, delay: 0.1 }}>
                    <Stack direction={'row'} sx={{ gap: 0.8, mb: 2.5, flexWrap: 'wrap' }}>
                        {[
                            { val: 'all',       label: 'Все',          count: messages.length },
                            { val: 'pending',   label: 'Запланировано',count: pendingCount    },
                            { val: 'recurring', label: 'Активные',     count: recurringCount  },
                            { val: 'history',   label: 'История',      count: historyCount    },
                            { val: 'inactive',  label: 'Неактивные',   count: inactiveCount   },
                        ].map(opt => (
                            <Box
                                key={opt.val}
                                onClick={() => setFilter(opt.val)}
                                sx={{
                                    px: 1.4, py: 0.7,
                                    borderRadius: 'var(--joy-radius-md)',
                                    border: '1px solid',
                                    borderColor: filter === opt.val ? 'var(--joy-palette-primary-500)' : 'var(--joy-palette-divider)',
                                    bgcolor: filter === opt.val ? 'rgba(162,38,226,0.1)' : 'var(--joy-palette-background-level1)',
                                    cursor: 'pointer', transition: 'all .15s',
                                    display: 'flex', alignItems: 'center', gap: 0.7,
                                    '&:hover': { borderColor: 'var(--joy-palette-primary-400)' },
                                }}
                            >
                                <Typography sx={{
                                    fontSize: 12, fontWeight: filter === opt.val ? 700 : 500,
                                    color: filter === opt.val ? 'var(--joy-palette-primary-300)' : 'var(--joy-palette-text-secondary)',
                                }}>
                                    {opt.label}
                                </Typography>
                                {opt.count > 0 && (
                                    <Box sx={{
                                        px: 0.7, py: 0.1,
                                        borderRadius: '6px',
                                        bgcolor: filter === opt.val ? 'rgba(162,38,226,0.2)' : 'var(--joy-palette-background-level2)',
                                    }}>
                                        <Typography sx={{
                                            fontSize: 10, fontWeight: 700,
                                            color: filter === opt.val ? 'var(--joy-palette-primary-300)' : 'var(--joy-palette-text-tertiary)',
                                        }}>
                                            {opt.count}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        ))}
                    </Stack>
                </motion.div>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <MemeLoader />
                    </Box>
                ) : filtered.length === 0 ? (
                    <Box sx={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2,
                        bgcolor: 'var(--joy-palette-background-level1)',
                        border: '1px solid var(--joy-palette-divider)',
                        borderRadius: 'var(--joy-radius-xl)',
                    }}>
                        <FormatQuoteRoundedIcon sx={{ fontSize: 36, color: 'var(--joy-palette-text-tertiary)' }} />
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'var(--joy-palette-text-secondary)' }}>
                                {filter === 'all' ? 'Нет сообщений' : 'Ничего не найдено'}
                            </Typography>
                            {filter === 'all' && (
                                <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', mt: 0.4 }}>
                                    Добавьте первое мотивационное сообщение
                                </Typography>
                            )}
                        </Box>
                        {filter === 'all' && canCreate && (
                            <Button
                                size="sm"
                                startDecorator={<AddRoundedIcon sx={{ fontSize: 14 }} />}
                                onClick={() => setEditModal({ open: true, message: null, loading: false, error: '' })}
                                sx={{
                                    background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                                    color: '#fff', fontWeight: 700, border: 'none',
                                    boxShadow: '0 3px 10px rgba(162,38,226,0.3)',
                                }}
                            >
                                Добавить сообщение
                            </Button>
                        )}
                    </Box>
                ) : (
                    <motion.div variants={stagger} initial="hidden" animate="visible">
                        <Stack spacing={1.2}>
                            {filtered.map(msg => (
                                <MessageCard
                                    key={msg.id}
                                    message={msg}
                                    canCreate={canCreate}
                                    canUpdate={canUpdate}
                                    canDelete={canDelete}
                                    onEdit={(m) => setEditModal({ open: true, message: m, loading: false, error: '' })}
                                    onDelete={(m) => setDeleteModal({ open: true, message: m, loading: false })}
                                    onToggle={handleToggle}
                                />
                            ))}
                        </Stack>
                    </motion.div>
                )}
            </Box>

            <MessageModal
                open={editModal.open}
                message={editModal.message}
                loading={editModal.loading}
                error={editModal.error}
                token={token}
                canCreate={canCreate}
                canUpdate={canUpdate}
                onClose={() => setEditModal({ open: false, message: null, loading: false, error: '' })}
                onSave={handleSave}
            />

            <DeleteModal
                open={deleteModal.open}
                loading={deleteModal.loading}
                onClose={() => setDeleteModal({ open: false, message: null, loading: false })}
                onConfirm={handleDelete}
            />
        </motion.div>
    );
}